import React, { useState } from 'react';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Button,
  MoreHorizIcon,
  Tooltip,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import ContextMenu from '../ui/ContextMenu';
import {
  collapseBreadcrumbSegments,
  MAX_VISIBLE_SEGMENTS,
  type BreadcrumbSegment,
} from './collapseBreadcrumbSegments';

interface PageBreadcrumbProps {
  segments: BreadcrumbSegment[];
}

/** One breadcrumb segment; shows the full label in a tooltip only when truncated. */
function SegmentItem({ segment }: { segment: BreadcrumbSegment }) {
  const navigate = useNavigate();
  const [isTruncated, setIsTruncated] = useState(false);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const element = event.currentTarget;
    setIsTruncated(element.scrollWidth > element.clientWidth);
  };

  return (
    <BreadcrumbsItem
      truncate
      data-testid={`breadcrumb-segment-${segment.label}`}
    >
      <Tooltip content={isTruncated ? segment.label : ''}>
        {segment.to ? (
          <Button
            size="sm"
            variant="ghost"
            className="block h-auto max-w-full truncate px-0 text-left font-normal text-content-secondary underline-offset-2 hover:bg-transparent hover:text-content-accent hover:underline"
            onClick={() => navigate(segment.to!)}
            onMouseEnter={handleMouseEnter}
          >
            {segment.label}
          </Button>
        ) : (
          <span
            className="block max-w-full truncate"
            onMouseEnter={handleMouseEnter}
          >
            {segment.label}
          </span>
        )}
      </Tooltip>
    </BreadcrumbsItem>
  );
}

export const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({ segments }) => {
  const navigate = useNavigate();
  const [collapseAnchor, setCollapseAnchor] = useState<HTMLElement | null>(
    null
  );
  const { hidden } = collapseBreadcrumbSegments(segments);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        maxItems={MAX_VISIBLE_SEGMENTS}
        itemsBeforeCollapse={1}
        itemsAfterCollapse={2}
        renderCollapsed={() => (
          <Button
            size="sm"
            variant="ghost"
            aria-label="Show full path"
            aria-haspopup="menu"
            onClick={event => setCollapseAnchor(event.currentTarget)}
            data-testid="breadcrumb-collapse-button"
            className="size-5 px-0 text-content-secondary hover:bg-transparent hover:text-content-primary"
          >
            <MoreHorizIcon className="size-3.5" aria-hidden="true" />
          </Button>
        )}
      >
        {segments.map((segment, index) => (
          <SegmentItem key={`${index}-${segment.label}`} segment={segment} />
        ))}
      </Breadcrumbs>
      {hidden.length > 0 && (
        <ContextMenu
          open={Boolean(collapseAnchor)}
          onClose={() => setCollapseAnchor(null)}
          anchorEl={collapseAnchor}
          items={hidden.map((segment, index) => ({
            key: `breadcrumb-hidden-${index}`,
            label: segment.label,
            disabled: !segment.to,
            onClick: () => {
              if (segment.to) {
                navigate(segment.to);
              }
            },
          }))}
        />
      )}
    </>
  );
};
